import moment from "moment";
import "moment-timezone";

export const getTimeDurationInMinutes = (
  startDateTime: any,
  endDateTime: any,
) => {
  // Calculate the duration by extracting the time part
  const time1 = startDateTime.format("HH:mm");
  const time2 = endDateTime.format("HH:mm");

  //Convert Back to Moment
  const momentTime1 = moment(time1, "HH:mm");
  const momentTime2 = moment(time2, "HH:mm");

  //calculate the duration in minutes
  let durationInMinutes = momentTime2.diff(momentTime1, "minutes");

  // Handle midnight crossing (e.g., 11:00 PM to 12:00 AM next day)
  if (durationInMinutes < 0) {
    durationInMinutes += 24 * 60; // Add 24 hours in minutes
  }

  return durationInMinutes;
};

export const getTimeDurationInMinutesFromTime = (
  startTime: string,
  endTime: string,
) => {
  //Convert Back to Moment
  const momentTime1 = moment(startTime, "HH:mm");
  const momentTime2 = moment(endTime, "HH:mm");

  //calculate the duration in minutes
  const durationInMinutes = momentTime2.diff(momentTime1, "minutes");

  return durationInMinutes;
};

// Function to adjust the event start time based on the local time zone offset
export const adjustWeekDayForTimeZone = (
  eventStartUtcString: string,
  initialTimezoneOffset: number | null,
) => {
  const eventStartTimeUtc = moment.utc(eventStartUtcString);
  const eventStartTimeLocal = moment.utc(eventStartTimeUtc).local();
  let adjustedDay = 0;

  //to shift the recurrence days one day forward, since the UTC conversion causes the time to shift into the next day and vice versa
  if (eventStartTimeLocal.day() > eventStartTimeUtc.day()) {
    adjustedDay = -1;
  } else if (eventStartTimeLocal.day() < eventStartTimeUtc.day()) {
    adjustedDay = +1;
  }

  //further adjust to show like US one in Australia and vice versa only for opposite timezones
  const localTimezoneOffsetInHours = moment().utcOffset() / 60;
  if (
    initialTimezoneOffset &&
    initialTimezoneOffset !== localTimezoneOffsetInHours
  ) {
    adjustedDay =
      adjustedDay +
      AdjustSameDayAcrossTimezones(eventStartUtcString, initialTimezoneOffset);
  }

  return adjustedDay;
};

export const AdjustSameDayAcrossTimezones = (
  utcDate: string,
  initialTimezoneOffset: number,
) => {
  // Convert the UTC date to a moment object in UTC
  const utcMoment = moment.utc(utcDate);

  // Convert the UTC time to the initial time zone (based on the offset)
  const initialTimeZoneMoment = utcMoment
    .clone()
    .utcOffset(initialTimezoneOffset);

  // Get the weekday in the initial time zone
  const initialDayOfWeek = initialTimeZoneMoment.day();

  // Convert the UTC time to the local time zone (browser time zone)
  const localMoment = utcMoment.clone().local();

  // Get the weekday in the local time zone
  const localDayOfWeek = localMoment.day();
  //console.log("Local Moment: ", localDayOfWeek);

  // Compare the weekdays (0 = Sunday, 6 = Saturday)
  return localDayOfWeek - initialDayOfWeek;
};

export const calculateUTCOffset = (date: string) => {
  // Create a new Date object from the passed date argument
  const targetDate = new Date(date);

  // Get the timezone offset in minutes for the specified date
  const offsetMinutes = targetDate.getTimezoneOffset(); // Offset in minutes

  // Convert the offset to hours (negative for UTC+)
  const offsetHours = -offsetMinutes / 60;

  // Return the calculated offset in hours
  return offsetHours;
};

export const lightenColor = (hex: string, percent: number) => {
  // Convert hex to RGB
  const num = parseInt(hex.slice(1), 16);
  const r = (num >> 16) + Math.round((255 - (num >> 16)) * (percent / 100));
  const g =
    ((num >> 8) & 0x00ff) +
    Math.round((255 - ((num >> 8) & 0x00ff)) * (percent / 100));
  const b =
    (num & 0x0000ff) + Math.round((255 - (num & 0x0000ff)) * (percent / 100));

  // Ensure RGB values are within range and convert back to hex
  return `#${(
    (1 << 24) +
    (Math.min(255, r) << 16) +
    (Math.min(255, g) << 8) +
    Math.min(255, b)
  )
    .toString(16)
    .slice(1)}`;
};

// Function to darken a color by a percentage
export const darkenColor = (color: string, percent: number): string => {
  // Remove the '#' if present
  const hex = color.replace("#", "");

  // Parse the color
  const num = parseInt(hex, 16);
  const r = Math.max(
    0,
    ((num >> 16) & 0x00ff) -
      Math.round(((num >> 16) & 0x00ff) * (percent / 100)),
  );
  const g = Math.max(
    0,
    ((num >> 8) & 0x00ff) - Math.round(((num >> 8) & 0x00ff) * (percent / 100)),
  );
  const b = Math.max(
    0,
    (num & 0x0000ff) - Math.round((num & 0x0000ff) * (percent / 100)),
  );

  return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
};

// Function to get theme-appropriate background color
export const getThemeAwareBackgroundColor = (
  baseColor: string | null,
  fallbackColor: string,
  isDarkMode: boolean,
): string => {
  const colorToUse = baseColor || fallbackColor;
  if (isDarkMode) {
    return darkenColor(colorToUse, 30); // Darken by 30% in dark mode (reduced from 70%)
  } else {
    return lightenColor(colorToUse, 85); // Lighten by 85% in light mode (reduced from 90%)
  }
};

export const formatDate = (date: Date): string =>
  date.toISOString().split("T")[0];

export type DateFormat =
  | "MM/DD/YYYY"
  | "DD/MM/YYYY"
  | "YYYY-MM-DD"
  | "MMM DD, YYYY"
  | "MMMM DD, YYYY"
  | "MM/DD/YYYY HH:mm"
  | "MM/DD/YYYY hh:mm A"
  | "hh:mm A"
  | "DAY MM/DD/YYY";

export const dateFormate = (
  date: string | Date | number | null | undefined,
  format: DateFormat = "MM/DD/YYYY",
  fallback: string = "-",
): string => {
  if (!date) return fallback;

  try {
    const dateObj = new Date(date);

    if (isNaN(dateObj.getTime())) {
      return fallback;
    }

    const month = dateObj.getMonth() + 1;
    const day = dateObj.getDate();
    const year = dateObj.getFullYear();
    const hours = dateObj.getHours();
    const minutes = dateObj.getMinutes();
    const days = dateObj.getDay();

    const monthNames = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];

    const monthNamesFull = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];

    const dayNames = [
      "Sunday",
      "Monday",
      "Tuesday",
      "Wednesday",
      "Thursday",
      "Friday",
      "Saturday",
    ];

    switch (format) {
      case "MM/DD/YYYY":
        return `${String(month).padStart(2, "0")}/${String(day).padStart(
          2,
          "0",
        )}/${year}`;

      case "DD/MM/YYYY":
        return `${String(day).padStart(2, "0")}/${String(month).padStart(
          2,
          "0",
        )}/${year}`;

      case "YYYY-MM-DD":
        return `${year}-${String(month).padStart(2, "0")}-${String(
          day,
        ).padStart(2, "0")}`;

      case "MMM DD, YYYY":
        return `${monthNames[month - 1]} ${String(day).padStart(
          2,
          "0",
        )}, ${year}`;

      case "MMMM DD, YYYY":
        return `${monthNamesFull[month - 1]} ${day}, ${year}`;

      case "MM/DD/YYYY HH:mm":
        return `${String(month).padStart(2, "0")}/${String(day).padStart(
          2,
          "0",
        )}/${year} ${String(hours).padStart(2, "0")}:${String(minutes).padStart(
          2,
          "0",
        )}`;

      case "MM/DD/YYYY hh:mm A":
        const ampm = hours >= 12 ? "PM" : "AM";
        const twelveHour = hours % 12 || 12;
        return `${String(month).padStart(2, "0")}/${String(day).padStart(
          2,
          "0",
        )}/${year} ${String(twelveHour).padStart(2, "0")}:${String(
          minutes,
        ).padStart(2, "0")} ${ampm}`;

      case "hh:mm A":
        const ampm2 = hours >= 12 ? "PM" : "AM";
        const twelveHour2 = hours % 12 || 12;
        return `${String(twelveHour2).padStart(2, "0")}:${String(minutes).padStart(2, "0")} ${ampm2}`;

      case "DAY MM/DD/YYY":
        return `${dayNames[days]} - ${String(month).padStart(2, "0")}/${String(
          day,
        ).padStart(2, "0")}/${year}`;

      default:
        return `${String(month).padStart(2, "0")}/${String(day).padStart(
          2,
          "0",
        )}/${year}`;
    }
  } catch (error) {
    return fallback;
  }
};
